import { updateAtom } from "../lib/atom-crdt"
import { applyTo as pass, curry, pipe } from "ramda"
import { CandidateID, PollCRDT, Candidate, ballot, emptyPoll } from "./PollCRDT"
import { test, equals, expect } from "@benchristel/taste"

export const withCandidateDeleted = curry(
  (id: CandidateID, poll: PollCRDT): PollCRDT => ({
    ...poll,
    candidates: {
      ...poll.candidates,
      [id]: null,
    },
  }),
)

export const withTitle = curry(
  (value: string, poll: PollCRDT): PollCRDT => ({
    ...poll,
    title: updateAtom(poll.title, value),
    initial: false,
  }),
)

export const withCandidate = curry(
  ({ id, content }: Candidate, poll: PollCRDT): PollCRDT => ({
    ...poll,
    candidates: { ...poll.candidates, [id]: content },
  }),
)

export const withBallot = curry(
  (agentID: string, ranking: string[], poll: PollCRDT): PollCRDT => ({
    ...poll,
    ballots: {
      ...poll.ballots,
      [agentID]: updateAtom(ballot(poll.ballots, agentID), ranking),
    },
  }),
)

test("withBallot", {
  "adds a ballot for a new voter"() {
    const agentID = "the-agent-id"
    const poll = withBallot(agentID, [], emptyPoll())
    expect(poll.ballots[agentID]?.value, equals, [])
  },

  "preserves existing ballots when adding a new one"() {
    const poll: PollCRDT = pass(
      emptyPoll(),
      pipe(
        withBallot("agent1", ["thai-food-1"]),
        withBallot("agent2", ["bibimbap-2"]),
      ),
    )

    expect(poll.ballots["agent1"]?.value, equals, ["thai-food-1"])
    expect(poll.ballots["agent2"]?.value, equals, ["bibimbap-2"])
  },

  "updates the ballot for an existing voter"() {
    const poll: PollCRDT = pass(
      emptyPoll(),
      pipe(
        withBallot("agent1", ["first", "second"]),
        withBallot("agent1", ["second", "first"]),
      ),
    )
    expect(poll.ballots["agent1"]?.value, equals, ["second", "first"])
  },

  "updates the generation of the ballot atom"() {
    const poll: PollCRDT = pass(
      emptyPoll(),
      pipe(withBallot("agent1", []), withBallot("agent1", [])),
    )
    expect(poll.ballots["agent1"]?.generation, equals, 2)
  },
})

test("withCandidateDeleted", {
  "displays a candidate as deleted"() {
    expect(
      withCandidateDeleted(
        "sch-lemaz-el",
        withCandidate({ id: "sch-lemaz-el", content: "Subaru" }, emptyPoll()),
      ).candidates,
      equals,
      { "sch-lemaz-el": null },
    )
  },
})
